/**
 * NewMovementModal — Muhasebe → + Yeni Hareket (iter98 Fix Pkg 03.1).
 *
 * Kullanıcı sağ üst "+ Yeni Hareket" butonundan buraya girer:
 *   - Hareket Türü: Müşteri Ödemesi | Tedarikçi Ödemesi
 *   - Müşteri/Tedarikçi autocomplete (elle yazılabilir + öneriler; yeni entity yaratmaz)
 *   - Tutar
 *   - Ödeme Şekli: Nakit / Kredi Kartı / Kredi Kartı - Mail Order / Çek
 *   - Çek ise: no + banka + vade + firma
 *
 * POS Komisyonu:
 *   - Müşteri + Kredi Kartı/Mail Order → backend otomatik %4 POS gider oluşturur (idempotent)
 *   - Kullanıcı sadece brüt tutarı girer, komisyon hesabı görünür (bilgi amaçlı)
 *
 * onSaved: parent parent'e yeni doc, POS expense bilgisi taşınır (varsa alert)
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Modal, Btn, Input, Select } from "@/components/UI";
import { Search, Plus, X, Loader2 } from "lucide-react";
import { todayTR } from "@/lib/date";
import { OwnCompanyOptions } from "@/lib/branding";

const trLower = (s) => (s || "").toLocaleLowerCase("tr");
const money = (n) => Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function NewMovementModal({ open, onClose, onSaved }) {
  const [type, setType] = useState("customer_payment");
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [constants, setConstants] = useState(null);
  const [form, setForm] = useState(initialForm());
  const [busy, setBusy] = useState(false);

  function initialForm() {
    return {
      party_id: "", party_name: "",
      transaction_date: todayTR(),
      amount: 0,
      payment_method: "CASH",
      note: "",
      check_number: "", bank_name: "", check_due_date: "", check_company: "",
    };
  }

  useEffect(() => {
    if (!open) return;
    setType("customer_payment"); setForm(initialForm());
    (async () => {
      try {
        const [cs, ss, cn] = await Promise.all([
          api.get("/customers"),
          api.get("/suppliers"),
          api.get("/accounting/constants"),
        ]);
        setCustomers(Array.isArray(cs) ? cs : []);
        setSuppliers(Array.isArray(ss) ? ss : []);
        setConstants(cn);
      } catch (e) { console.error(e); }
    })();
  }, [open]);

  const list = type === "customer_payment" ? customers : suppliers;
  const rate = useMemo(() => {
    if (!constants) return 0.04;
    if (form.payment_method === "CREDIT_CARD") return constants.pos_credit_card_rate ?? 0.04;
    if (form.payment_method === "MAIL_ORDER")  return constants.pos_mail_order_rate ?? 0.04;
    return 0;
  }, [form.payment_method, constants]);
  const commission = useMemo(() => Math.round((Number(form.amount) || 0) * rate * 100) / 100, [form.amount, rate]);

  const save = async () => {
    if (busy) return;
    if (!form.party_id) { alert(type === "customer_payment" ? "Müşteri seçin (öneriden)" : "Tedarikçi seçin (öneriden)"); return; }
    if (!(Number(form.amount) > 0)) { alert("Tutar 0'dan büyük olmalı"); return; }
    if (form.payment_method === "CHECK") {
      if (!form.check_number || !form.bank_name || !form.check_due_date) {
        alert("Çek numarası, banka ve vade tarihi zorunlu"); return;
      }
    }
    setBusy(true);
    try {
      if (type === "customer_payment") {
        const payload = {
          customer_id: form.party_id,
          transaction_type: "PAYMENT",
          transaction_date: form.transaction_date,
          amount: Number(form.amount),
          payment_method: form.payment_method,
          note: form.note || null,
          check_number: form.check_number || null,
          bank_name: form.bank_name || null,
          check_due_date: form.check_due_date || null,
          check_company: form.payment_method === "CHECK" ? form.check_company : null,
          source_type: "manual",
        };
        const resp = await api.post("/accounting/customer-transactions", payload);
        if (resp.pos_commission_expense) {
          alert(`✓ Müşteri Ödemesi kaydedildi.\nOtomatik POS Komisyonu Gideri: ${money(resp.pos_commission_expense.amount)} TL (%${Math.round(rate*100)}) — Gelir/Gider'de görüntülenebilir.`);
        } else {
          alert("✓ Müşteri Ödemesi kaydedildi. Müşteri bakiyesi güncellendi.");
        }
      } else {
        const payload = {
          supplier_id: form.party_id,
          supplier_name: form.party_name,
          transaction_date: form.transaction_date,
          amount: Number(form.amount),
          payment_method: form.payment_method,
          note: form.note || null,
          check_number: form.check_number || null,
          bank_name: form.bank_name || null,
          check_due_date: form.check_due_date || null,
        };
        await api.post("/accounting/supplier-payment", payload);
        alert("✓ Tedarikçi Ödemesi kaydedildi. Gelir/Gider'de EXPENSE olarak görünür.");
      }
      onSaved?.();
      onClose?.();
    } catch (e) {
      alert(e.response?.data?.detail || "Kayıt başarısız");
    } finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={() => !busy && onClose?.()} title="Yeni Hareket" size="lg">
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-2 bg-slate-50 border border-slate-200 rounded p-2">
          {[
            { k: "customer_payment", l: "Müşteri Ödemesi", clr: "emerald" },
            { k: "supplier_payment", l: "Tedarikçi Ödemesi", clr: "rose" },
          ].map(t => (
            <button
              key={t.k} type="button"
              onClick={() => { setType(t.k); setForm(f => ({ ...f, party_id: "", party_name: "" })); }}
              className={`px-3 py-2 text-sm font-semibold rounded ${type === t.k
                ? (t.clr === "emerald" ? "bg-emerald-600 text-white" : "bg-rose-600 text-white")
                : "bg-white border border-slate-300 text-slate-600 hover:border-slate-400"}`}
              data-testid={`mv-type-${t.k}`}
            >
              {t.l}
            </button>
          ))}
        </div>

        <PartyPicker
          type={type} list={list}
          value={form.party_id} valueName={form.party_name}
          onPick={(id, name) => setForm(f => ({ ...f, party_id: id, party_name: name }))}
        />

        <div className="grid grid-cols-2 gap-3">
          <Input label="Tarih *" type="date" value={form.transaction_date}
                 onChange={e => setForm({ ...form, transaction_date: e.target.value })}
                 data-testid="mv-date"/>
          <Input label="Tutar (TL) *" type="number" step="0.01" value={form.amount}
                 onChange={e => setForm({ ...form, amount: e.target.value })}
                 data-testid="mv-amount"/>
          <Select label="Ödeme Şekli *" value={form.payment_method}
                  onChange={e => setForm({ ...form, payment_method: e.target.value })}
                  data-testid="mv-method">
            <option value="CASH">Nakit</option>
            <option value="CREDIT_CARD">Kredi Kartı</option>
            <option value="MAIL_ORDER">Kredi Kartı - Mail Order</option>
            <option value="CHECK">Çek</option>
          </Select>
          <Input label="Not (opsiyonel)" value={form.note}
                 onChange={e => setForm({ ...form, note: e.target.value })}
                 data-testid="mv-note"/>
        </div>

        {(form.payment_method === "CREDIT_CARD" || form.payment_method === "MAIL_ORDER") && type === "customer_payment" && (
          <div className="bg-amber-50 border border-amber-300 rounded p-3 text-xs space-y-0.5">
            <div className="font-semibold text-amber-900 flex items-center gap-1">
              <Plus size={12}/> POS Komisyonu Otomatik Hesaplanacak
            </div>
            <div className="text-amber-800 grid grid-cols-3 gap-2 mt-1.5">
              <div><span className="text-amber-600">Brüt Tahsilat: </span><b className="font-mono">{money(form.amount)} TL</b></div>
              <div><span className="text-amber-600">Komisyon (%{Math.round(rate*100)}): </span><b className="font-mono">{money(commission)} TL</b></div>
              <div><span className="text-amber-600">Müşteri Bakiye Etkisi: </span><b className="font-mono">-{money(form.amount)} TL</b></div>
            </div>
            <div className="text-amber-700 italic mt-1">Kaynak: Muhasebe → Gelir/Gider → Kategori "pos_komisyonu"</div>
          </div>
        )}

        {form.payment_method === "CHECK" && (
          <div className="grid grid-cols-2 gap-3 border-t pt-3">
            <Input label="Çek No *" value={form.check_number} onChange={e => setForm({ ...form, check_number: e.target.value })} data-testid="mv-check-no"/>
            <Input label="Banka *" value={form.bank_name} onChange={e => setForm({ ...form, bank_name: e.target.value })} data-testid="mv-check-bank"/>
            <Input label="Vade Tarihi *" type="date" value={form.check_due_date} onChange={e => setForm({ ...form, check_due_date: e.target.value })} data-testid="mv-check-due"/>
            {type === "customer_payment" && (
              <Select label="Çek Firması *" value={form.check_company} onChange={e => setForm({ ...form, check_company: e.target.value })} data-testid="mv-check-company">
                <OwnCompanyOptions />
              </Select>
            )}
          </div>
        )}
      </div>

      <div className="flex justify-end gap-2 mt-4">
        <Btn variant="secondary" onClick={onClose} disabled={busy}>Vazgeç</Btn>
        <Btn onClick={save} disabled={busy} data-testid="mv-save">
          {busy ? <><Loader2 size={12} className="animate-spin"/> Kaydediliyor…</> : "Kaydet"}
        </Btn>
      </div>
    </Modal>
  );
}

/** Customer/Supplier autocomplete — elle yazılır + mevcut önerilerden seçilir (yeni entity yaratmaz). */
function PartyPicker({ type, list, value, valueName, onPick }) {
  const [q, setQ] = useState(valueName || "");
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => { setQ(valueName || ""); }, [valueName]);
  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const norm = trLower;
  const suggestions = useMemo(() => {
    const nameKey = type === "customer_payment" ? "legal_name" : "name";
    const qn = norm(q);
    if (!qn) return (list || []).slice(0, 15);
    return (list || []).filter(x => norm(x[nameKey] || x.name || x.legal_name).includes(qn)).slice(0, 15);
  }, [q, list, type]);

  const label = type === "customer_payment" ? "Müşteri Adı *" : "Tedarikçi Adı *";
  const testid = type === "customer_payment" ? "mv-customer" : "mv-supplier";
  const displayName = (x) => x.legal_name || x.name || "—";

  return (
    <div className="relative" ref={boxRef} data-testid={testid}>
      <span className="text-xs font-medium uppercase tracking-wider text-slate-500 block mb-1">{label}</span>
      <div className="flex items-center gap-1.5 px-2 py-1.5 border border-slate-300 rounded bg-white">
        <Search size={12} className="text-slate-400"/>
        <input
          value={q}
          onFocus={() => setOpen(true)}
          onChange={e => { setQ(e.target.value); setOpen(true);
            // Kullanıcı manuel değiştirirse party_id temizle — sadece öneri seçilirse geçerli
            if (value) onPick("", e.target.value);
          }}
          placeholder={type === "customer_payment" ? "Müşteri adı yazın..." : "Tedarikçi adı yazın..."}
          className="flex-1 text-sm outline-none bg-transparent"
          data-testid={`${testid}-input`}
          autoComplete="off"
        />
        {value && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-mono">✓ seçili</span>}
      </div>
      {open && (
        <div className="absolute z-30 mt-1 w-full max-h-60 overflow-y-auto bg-white border border-slate-200 rounded shadow-lg">
          {suggestions.length === 0 ? (
            <div className="p-3 text-xs text-rose-700">
              <div className="font-medium mb-1">Bu {type === "customer_payment" ? "müşteri" : "tedarikçi"} sistemde bulunamadı.</div>
              <div className="text-slate-500 italic">Otomatik kayıt oluşturulmaz. Müşteriler / Tedarikçiler sayfasından önce entity eklenmelidir.</div>
            </div>
          ) : (
            suggestions.map(x => (
              <button
                key={x.id} type="button"
                onClick={() => { onPick(x.id, displayName(x)); setQ(displayName(x)); setOpen(false); }}
                className="w-full text-left px-3 py-1.5 text-xs hover:bg-indigo-50 border-b border-slate-100 last:border-0"
                data-testid={`${testid}-opt-${x.id}`}
              >
                <div className="font-medium text-slate-800">{displayName(x)}</div>
                {x.tax_no && <div className="text-[10px] text-slate-500 font-mono">VKN: {x.tax_no}</div>}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
